import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Typewriter from './Typewriter';

const Hero = () => {
  const [iframeLoaded, setIframeLoaded] = useState(false);

  return (
    <section className="relative w-full h-[100dvh] overflow-hidden bg-obsidian">
      {/* 1. OPTIMIZATION: STATIC FALLBACK LAYER */}
      <div 
        className={`absolute inset-0 z-0 transition-opacity duration-1000 ${iframeLoaded ? 'opacity-0' : 'opacity-100'}`}
        style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1620712943543-bcc4628c9757?q=80&w=1920&auto=format&fit=crop")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-obsidian/70 backdrop-blur-[4px]"></div>
      </div>

      {/* 2. 3D SPLINE LAYER */}
      <div className="absolute inset-0 w-full h-full z-0 overflow-hidden">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: iframeLoaded ? 1 : 0 }}
          transition={{ duration: 1.5 }}
          className="absolute w-[calc(100%+100px)] h-[calc(100%+100px)] -bottom-10 -right-10"
        >
          <iframe 
            src='https://my.spline.design/nexbotrobotcharacterconcept-MQaJDRqHKxJHg4Mx23VsRC9k/' 
            frameBorder='0' 
            width='100%' 
            height='100%' 
            className="w-full h-full pointer-events-auto scale-110 md:scale-100" 
            title="3D Robot" 
            onLoad={() => setIframeLoaded(true)}
            loading="lazy"
          ></iframe>
        </motion.div>
      </div>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-transparent to-obsidian z-10 pointer-events-none"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent z-10 pointer-events-none opacity-95"></div>

      {/* Hero Content */}
      <div className="relative z-20 container mx-auto px-6 h-full flex flex-col justify-end pb-16 md:pb-32 pointer-events-none">
        <motion.div 
          initial={{ opacity: 0, y: 50 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 1, delay: 0.5 }} 
          className="max-w-4xl pointer-events-auto"
        >
          <div className="flex items-center space-x-3 md:space-x-4 mb-4">
            <div className="h-[1px] w-8 md:w-12 bg-neon-cyan"></div>
            <Typewriter text="ENGINEERED BY MIDNIGHTNEURON" className="text-neon-cyan font-mono text-[9px] md:text-sm tracking-[0.2em] uppercase" speed={60} />
          </div>
          
          <div className="mb-6 md:mb-8 relative scanline-overlay inline-block w-full">
            <h1 className="font-orbitron font-black text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-white leading-[1.1]">
              <Typewriter text="DESIGNING" delay={400} speed={50} className="block" />
              <div className="min-h-[1.1em] py-1">
                <Typewriter 
                  text="INTELLIGENCE" 
                  delay={1200} 
                  speed={50} 
                  glow 
                  className="block text-white lg:text-transparent lg:bg-clip-text lg:bg-gradient-to-r lg:from-white lg:via-neon-cyan lg:to-white" 
                />
              </div>
            </h1>
          </div>

          <div className="mb-8 md:mb-10 max-w-xl">
            <Typewriter 
              text="A portfolio of AI-driven products, prototypes, and experiments — engineered end‑to‑end by MidnightNeuron." 
              delay={2000} 
              speed={25} 
              className="text-gray-300 text-sm md:text-xl font-light leading-relaxed border-l-2 border-neon-cyan/50 pl-4 md:pl-6 bg-obsidian/30 backdrop-blur-sm py-2 rounded-r-lg block" 
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="#projects" 
              className="px-6 md:px-8 py-4 bg-white text-black font-orbitron font-bold tracking-wider hover:bg-neon-cyan hover:text-black transition-all duration-300 flex items-center justify-center gap-2 group shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_20px_rgba(0,240,255,0.5)] text-sm md:text-base border-r-8 border-neon-cyan/20"
            >
              EXPLORE SYSTEMS 
              <SafeIcon icon={FiIcons.FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a 
              href="#contact" 
              className="px-6 md:px-8 py-4 border border-white/20 text-white font-orbitron font-bold tracking-wider hover:border-neon-cyan hover:text-neon-cyan transition-all duration-300 backdrop-blur-md flex items-center justify-center bg-black/20 text-sm md:text-base"
            >
              WORK WITH US
            </a>
          </div>
        </motion.div>
      </div>

      <motion.div 
        animate={{ y: [0, 10, 0] }} 
        transition={{ repeat: Infinity, duration: 2 }} 
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 pointer-events-none"
      >
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-widest hidden md:block">Scroll to scan</span>
        <div className="w-[1px] h-8 md:h-12 bg-gradient-to-b from-neon-cyan to-transparent"></div>
      </motion.div>
    </section>
  );
};

export default Hero;